import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../utils/axiosInstance";
import { motion, AnimatePresence } from "framer-motion";

const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const toUrl = (p) => { if (!p) return ""; if (p.startsWith("http")) return p; return `${API_BASE}${p.startsWith("/") ? p : "/" + p}`; };
const isImage = (p = "") => /\.(jpe?g|png|webp|gif)$/i.test(p);
const isPdf = (p = "") => /\.pdf$/i.test(p);
const fmtDate = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleDateString("rw-RW", { year:"numeric", month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" }); } catch { return d; } };
const priorityColor = (p) => ({ Urgent:"#dc2626", High:"#d97706", Normal:"#2563eb", Low:"#16a34a" }[p] || "#2563eb");

const AmatangazoIsibo = () => {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [selected, setSelected] = useState(null);
  const [search, setSearch]     = useState("");
  const [error, setError]       = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await axiosInstance.get("/amatangazo/for-isibo");
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError("Ntibyashobotse kubona amatangazo. Gerageza nanone.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const filtered = items.filter(x =>
    (x.title || "").toLowerCase().includes(search.toLowerCase()) ||
    (x.message || "").toLowerCase().includes(search.toLowerCase()) ||
    (x.forwarded_by_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (x.original_sender_name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ fontFamily:"system-ui,sans-serif", maxWidth:860, margin:"0 auto", padding:"0 4px" }}>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20, flexWrap:"wrap", gap:12 }}>
        <div>
          <h2 style={{ fontSize:22, fontWeight:700, color:"#1a2332", margin:0 }}>
            <i className="bi bi-megaphone-fill me-2" style={{ color:"#7c3aed" }}></i>
            Amatangazo Yoherejwe
          </h2>
          <p style={{ color:"#6b7280", fontSize:13, marginTop:4 }}>Amatangazo yoherejwe na Umudugudu — {items.length} itangazo</p>
        </div>
        <button onClick={fetchItems} disabled={loading}
          style={{ padding:"9px 18px", background:"#7c3aed", color:"#fff", border:"none", borderRadius:10, fontWeight:700, fontSize:13, cursor:"pointer", display:"flex", alignItems:"center", gap:6, minHeight:40 }}>
          <i className={`bi ${loading ? "bi-arrow-repeat" : "bi-arrow-clockwise"}`} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
          Vugurura
        </button>
      </div>

      {/* Search */}
      <div style={{ display:"flex", alignItems:"center", gap:8, background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:10, padding:"0 12px", marginBottom:16 }}>
        <i className="bi bi-search" style={{ color:"#94a3b8" }}></i>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Shakisha itangazo..."
          style={{ border:"none", background:"transparent", outline:"none", padding:"0.6rem 0.25rem", fontSize:"0.88rem", color:"#1e293b", width:"100%" }} />
        {search && <button onClick={() => setSearch("")} style={{ background:"none", border:"none", cursor:"pointer", color:"#94a3b8", padding:0 }}><i className="bi bi-x-circle-fill"></i></button>}
      </div>

      {error && (
        <div style={{ padding:"12px 16px", background:"#fef2f2", border:"1px solid #fecaca", borderRadius:10, color:"#dc2626", marginBottom:16, fontSize:14 }}>
          <i className="bi bi-exclamation-triangle-fill me-2"></i>{error}
          <button onClick={fetchItems} style={{ marginLeft:12, padding:"4px 12px", background:"#dc2626", color:"#fff", border:"none", borderRadius:6, cursor:"pointer", fontSize:12, fontWeight:600 }}>Gerageza nanone</button>
        </div>
      )}

      {loading && <div style={{ textAlign:"center", padding:"4rem 0" }}><div className="spinner-border" style={{ width:"3rem", height:"3rem", color:"#7c3aed" }} /><p style={{ marginTop:"1rem", color:"#64748b" }}>Amatangazo arimo gutegurwa...</p></div>}

      {!loading && !error && filtered.length === 0 && (
        <div style={{ textAlign:"center", padding:"5rem 2rem", background:"#fff", borderRadius:14, border:"1px solid #e5e7eb" }}>
          <i className="bi bi-megaphone" style={{ fontSize:48, color:"#d1d5db", display:"block", marginBottom:12 }}></i>
          <h5 style={{ color:"#374151", fontWeight:700 }}>{search ? "Nta tangazo ibonetse" : "Nta matangazo ahari"}</h5>
          <p style={{ color:"#9ca3af", fontSize:14 }}>{search ? "Hindura amagambo yo gushakisha" : "Umudugudu ntiyohereje amatangazo narimwe"}</p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {filtered.map((item, i) => (
            <motion.div key={item.id} initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.04 }}
              style={{ background:"#fff", border:"1px solid #e5e7eb", borderLeft:`4px solid ${priorityColor(item.priority)}`, borderRadius:12, padding:"1.25rem 1.5rem", boxShadow:"0 2px 8px rgba(0,0,0,0.05)", cursor:"pointer" }}
              onClick={() => setSelected(item)}
              onMouseEnter={e => e.currentTarget.style.boxShadow = "0 6px 20px rgba(124,58,237,0.1)"}
              onMouseLeave={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.05)"}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12, flexWrap:"wrap" }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6, flexWrap:"wrap" }}>
                    <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, color:"#fff", background:priorityColor(item.priority) }}>{item.priority || "Normal"}</span>
                    <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, background:"#dcfce7", color:"#15803d", border:"1px solid #bbf7d0" }}>✅ Yoherejwe</span>
                  </div>
                  {item.title && <div style={{ fontSize:16, fontWeight:700, color:"#1a2332", marginBottom:4 }}>{item.title}</div>}
                  <div style={{ fontSize:14, color:"#444", lineHeight:1.6, marginBottom:8, overflow:"hidden", textOverflow:"ellipsis", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical" }}>
                    {item.message}
                  </div>
                  <div style={{ display:"flex", gap:12, flexWrap:"wrap", fontSize:12, color:"#9ca3af" }}>
                    <span><i className="bi bi-person-fill me-1" style={{ color:"#7c3aed" }}></i>Akagari: {item.original_sender_name || "—"}</span>
                    <span><i className="bi bi-arrow-right-circle-fill me-1" style={{ color:"#0f766e" }}></i>Umudugudu: {item.forwarded_by_name || "—"}</span>
                    <span><i className="bi bi-clock me-1"></i>{fmtDate(item.forwarded_at)}</span>
                  </div>
                </div>
                <button onClick={e => { e.stopPropagation(); setSelected(item); }}
                  style={{ padding:"6px 14px", background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:8, color:"#2563eb", fontSize:12, fontWeight:600, cursor:"pointer", flexShrink:0 }}>
                  <i className="bi bi-eye me-1"></i>Reba
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <AnimatePresence>
        {selected && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            onClick={() => setSelected(null)}
            style={{ position:"fixed", inset:0, zIndex:2000, background:"rgba(0,0,0,0.6)", backdropFilter:"blur(8px)", display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }}>
            <motion.div initial={{ scale:0.95, y:20, opacity:0 }} animate={{ scale:1, y:0, opacity:1 }} exit={{ scale:0.95, opacity:0 }}
              onClick={e => e.stopPropagation()}
              style={{ background:"#fff", borderRadius:20, width:"100%", maxWidth:600, maxHeight:"88vh", overflow:"auto", boxShadow:"0 25px 60px rgba(0,0,0,0.25)" }}>
              <div style={{ padding:"1.25rem 1.5rem", borderBottom:"1px solid #e5e7eb", display:"flex", justifyContent:"space-between", alignItems:"center", position:"sticky", top:0, background:"#fff", zIndex:5 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <div style={{ width:40, height:40, borderRadius:12, background:"linear-gradient(135deg,#7c3aed,#6d28d9)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <i className="bi bi-megaphone-fill" style={{ color:"#fff", fontSize:"1rem" }}></i>
                  </div>
                  <div>
                    <div style={{ fontWeight:800, color:"#1e293b", fontSize:"0.95rem" }}>Amakuru y'Itangazo</div>
                    <div style={{ fontSize:"0.75rem", color:"#64748b" }}>Yoherejwe na {selected.forwarded_by_name || "Umudugudu"}</div>
                  </div>
                </div>
                <button onClick={() => setSelected(null)} style={{ background:"none", border:"none", fontSize:"1.3rem", cursor:"pointer", color:"#94a3b8" }}>✕</button>
              </div>

              <div style={{ padding:"1.5rem" }}>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:"1rem" }}>
                  <span style={{ padding:"4px 12px", borderRadius:20, fontSize:12, fontWeight:700, color:"#fff", background:priorityColor(selected.priority) }}>{selected.priority || "Normal"}</span>
                  <span style={{ padding:"4px 12px", borderRadius:20, fontSize:12, fontWeight:700, background:"#dcfce7", color:"#15803d", border:"1px solid #bbf7d0" }}>✅ Yoherejwe</span>
                </div>

                {selected.title && <h4 style={{ fontWeight:800, color:"#1e293b", marginBottom:"1rem" }}>{selected.title}</h4>}

                <div style={{ background:"#f9fafb", borderRadius:10, padding:"1rem", marginBottom:"1rem", fontSize:14, color:"#374151", lineHeight:1.8, whiteSpace:"pre-wrap" }}>
                  {selected.message}
                </div>

                {selected.attachment && (
                  <div style={{ marginBottom:"1rem" }}>
                    {isImage(selected.attachment) && (
                      <img src={toUrl(selected.attachment)} alt="attachment" style={{ maxWidth:"100%", maxHeight:200, borderRadius:8, objectFit:"contain", border:"1px solid #e5e7eb" }} />
                    )}
                    {isPdf(selected.attachment) && (
                      <a href={toUrl(selected.attachment)} target="_blank" rel="noopener noreferrer"
                        style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"8px 14px", background:"#f0f9ff", border:"1px solid #bae6fd", borderRadius:8, color:"#0369a1", textDecoration:"none", fontWeight:600, fontSize:13 }}>
                        <i className="bi bi-file-pdf-fill"></i> Fungura PDF
                      </a>
                    )}
                  </div>
                )}

                {/* Forwarding chain */}
                <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:10, padding:"1rem", marginBottom:"1rem" }}>
                  <div style={{ fontSize:12, fontWeight:700, color:"#15803d", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:8 }}>Inzira y'Itangazo</div>
                  <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", fontSize:13 }}>
                    <span style={{ padding:"4px 10px", background:"#dbeafe", color:"#1d4ed8", borderRadius:8, fontWeight:600 }}>
                      <i className="bi bi-person-fill me-1"></i>{selected.original_sender_name || "Akagari"}
                    </span>
                    <i className="bi bi-arrow-right" style={{ color:"#94a3b8" }}></i>
                    <span style={{ padding:"4px 10px", background:"#f3e8ff", color:"#7c3aed", borderRadius:8, fontWeight:600 }}>
                      <i className="bi bi-arrow-right-circle-fill me-1"></i>{selected.forwarded_by_name || "Umudugudu"}
                    </span>
                    <i className="bi bi-arrow-right" style={{ color:"#94a3b8" }}></i>
                    <span style={{ padding:"4px 10px", background:"#dcfce7", color:"#15803d", borderRadius:8, fontWeight:600 }}>
                      <i className="bi bi-person-check-fill me-1"></i>Isibo (Wowe)
                    </span>
                  </div>
                  <div style={{ fontSize:12, color:"#6b7280", marginTop:8 }}>
                    <i className="bi bi-clock me-1"></i>Yoherejwe: {fmtDate(selected.forwarded_at)}
                  </div>
                </div>

                <button onClick={() => setSelected(null)}
                  style={{ width:"100%", padding:"0.85rem", border:"1.5px solid #e2e8f0", borderRadius:12, background:"#f8fafc", color:"#64748b", fontWeight:700, cursor:"pointer", fontSize:"0.9rem" }}>
                  Funga
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AmatangazoIsibo;
